import React, { useMemo } from 'react'
import { useFormikContext } from 'formik'
import tw from 'twin.macro'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { Button } from 'src/components/shared/Button'
import { Tags } from 'src/components/tags/Tags'
import { Tag } from 'src/interfaces/tag'
import { OnboardingTemplate } from 'src/interfaces/onboarding'
import { Market } from 'src/interfaces/market'
import { Divider, Typography } from '@mui/material'
import { getOnBoardingStepsNames } from '../InviteWorkersUtils'

interface InviteWorkersViaMessageFormContentProps {
  isLoading: boolean
  selectedTags: Array<Tag>
  handleSelectTag(tag: Tag): void
  handleRemoveTag(tag: Tag): void
  onboardingTemplates: OnboardingTemplate[]
  markets: Market[]
}

interface InviteWorkersViaMessageFormValues {
  collectDataTemplateId: string
  partnerMarketId: string
  firstName: string
  lastName: string
  phone: string
  email: string
}

const PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

export const InviteWorkersViaMessageFormContent: React.FC<
  InviteWorkersViaMessageFormContentProps
> = ({
  isLoading,
  selectedTags,
  handleSelectTag,
  handleRemoveTag,
  onboardingTemplates,
  markets,
}) => {
  const { values, handleSubmit } = useFormikContext<InviteWorkersViaMessageFormValues>()

  const stepNames = useMemo(() => {
    return getOnBoardingStepsNames(onboardingTemplates, values.collectDataTemplateId)
  }, [values.collectDataTemplateId, onboardingTemplates])

  const showOnboardingSteps = Boolean(values.collectDataTemplateId && stepNames.trim())

  const selectedTemplate = useMemo(
    () => onboardingTemplates.find((t) => t.id === values.collectDataTemplateId),
    [values.collectDataTemplateId, onboardingTemplates],
  )

  return (
    <>
      <div css={tw`px-4 pt-5`}>
        <div css={tw`grid grid-cols-2 gap-4 mb-5`}>
          <FormikSelect
            name="collectDataTemplateId"
            label="Onboarding flow"
            placeholder="Select flow"
            options={onboardingTemplates.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
          />
          <FormikSelect
            name="partnerMarketId"
            label="Market"
            placeholder="Select market"
            options={markets.map((item) => ({
              label: item.title,
              value: item.id,
            }))}
          />
        </div>
        {showOnboardingSteps && (
          <div css={tw`flex mt-3 p-0`}>
            <div css={tw`w-1 bg-[#8A2BE2] rounded mr-0`} />
            <div css={tw`pl-3 flex-1`}>
              <Typography css={tw`text-sm font-semibold mb-1`}>
                {selectedTemplate?.name || 'Selected Flow'} Step(s)
              </Typography>
              <Typography css={tw`text-sm text-[#847E96]`}>{stepNames}</Typography>
            </div>
          </div>
        )}
        <Divider css={tw`mt-5 w-full border-[#EDEDED]`} />
        <div
          css={tw`grid grid-cols-2 gap-5 border-0 border-b border-solid border-[#EDEDED] mt-5 mb-5 pb-5`}
        >
          <FormikTextField
            name="firstName"
            css={tw`w-full`}
            label="First name"
            placeholder="Enter worker's first name"
          />
          <FormikTextField
            name="lastName"
            label="Last name"
            css={tw`w-full`}
            placeholder="Enter worker's last name"
          />
          <FormikTextField
            name="phone"
            label="Phone number"
            css={tw`w-full`}
            mask={PHONE_MASK}
            placeholder="Enter worker's phone number"
          />
          <FormikTextField
            name="email"
            label="Email"
            css={tw`w-full`}
            placeholder="Enter worker's email"
          />
        </div>

        <Tags
          tags={selectedTags}
          className={tw`mb-0 pb-0 border-0`}
          onSelectTag={handleSelectTag}
          onRemoveTag={handleRemoveTag}
        />
      </div>

      <div
        css={tw`flex flex-col justify-end items-end border-0 border-t border-solid border-[#EDEDED] mt-8 p-4 pb-3 pt-3`}
      >
        <Button variant="contained" size="small" loading={isLoading} onClick={() => handleSubmit()}>
          Send invite
        </Button>
      </div>
    </>
  )
}
